import { getPaymentMethodFieldsForAccount } from "../constants/bankOptionsByCountry";

export interface StructuredDataDisplayEntry {
  key: string;
  label: string;
  value: string;
}

const LEGACY_LABELS: Record<string, string> = {
  bank_name: "Banco",
  bankName: "Banco",
  institution_name: "Institución",
  account_number: "Número de cuenta",
  accountNumber: "Número de cuenta",
  account_type: "Tipo de cuenta",
  accountType: "Tipo de cuenta",
};

/** Converts camelCase to title case for display (e.g. numeroDocumento → Número documento). */
function humanizeKey(key: string): string {
  const withSpaces = key.replace(/([A-Z])/g, " $1").trim();
  const lower = withSpaces.toLowerCase();
  const capitalized = lower.replace(/\b\w/g, (c) => c.toUpperCase());
  const replacements: [RegExp, string][] = [
    [/numero\b/gi, "Número de"],
    [/tipo\b/gi, "Tipo de"],
    [/banco\b/gi, "Banco"],
    [/documento\b/gi, "documento"],
    [/cuenta\b/gi, "cuenta"],
    [/celular\b/gi, "celular"],
    [/direccion\b/gi, "Dirección"],
    [/identificador\b/gi, "Identificador"],
    [/email\b/gi, "Email"],
    [/llave\b/gi, "Llave"],
    [/routing\b/gi, "Routing"],
    [/swift\b/gi, "SWIFT"],
    [/bic\b/gi, "BIC"],
    [/iban\b/gi, "IBAN"],
    [/clabe\b/gi, "CLABE"],
    [/cbu\b/gi, "CBU"],
    [/cvu\b/gi, "CVU"],
  ];
  let out = capitalized;
  for (const [re, repl] of replacements) {
    out = out.replace(re, repl);
  }
  return out.trim() || key;
}

/**
 * Builds a list of { key, label, value } from structured_data for display in the detail modal.
 * Uses payment method field config for labels when available; falls back to legacy labels or humanized key.
 * If translateLabel is provided, the returned label is the result of translateLabel(key, fallbackLabel).
 */
export function getStructuredDataDisplayEntries(
  sd: Record<string, unknown> | null | undefined,
  countryCode: string,
  translateLabel?: (key: string, fallbackLabel: string) => string
): StructuredDataDisplayEntry[] {
  if (!sd || typeof sd !== "object") return [];

  const bankName = (sd.bank_name as string) ?? (sd.bankName as string);
  const methodFieldsConfig = getPaymentMethodFieldsForAccount(countryCode, bankName ?? "");

  const labelByKey: Record<string, string> = { ...LEGACY_LABELS };
  for (const field of methodFieldsConfig) {
    labelByKey[field.id] = field.label;
  }

  const entries: StructuredDataDisplayEntry[] = [];
  const keys = Object.keys(sd);

  for (const key of keys) {
    const raw = sd[key];
    if (raw === null || raw === undefined) continue;
    const value = typeof raw === "string" ? raw.trim() : String(raw);
    const fallbackLabel = labelByKey[key] ?? humanizeKey(key);
    const label = translateLabel ? translateLabel(key, fallbackLabel) : fallbackLabel;
    entries.push({ key, label, value: value || "—" });
  }

  return entries;
}
